import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  selectIsConnectedToRoom,
  useHMSActions,
  useHMSStore,
  selectPeers,
  selectLocalPeer,
  useVideo,
  useDevices,
  DeviceType,
  selectPeersScreenSharing,
  selectIsPeerAudioEnabled,
  selectIsPeerVideoEnabled,
  selectScreenShareByPeerID,
  selectIsLocalScreenShared,
  HMSActions,
  useAVToggle,
} from "@100mslive/react-sdk";

import {
  CameraIcon,
  EllipsisVertical,
  ExpandIcon,
  Mic,
  Mic2Icon,
  MicOff,
  PhoneCall,
  ScreenShare,
  ScreenShareOff,
  Settings,
  ShrinkIcon,
  SquareArrowDownRight,
  SquareArrowOutDownRightIcon,
  Video,
  VideoOff,
  X,
} from "lucide-react";
import CustomImage from "../CustomImage";
import { ChatContext } from "../ChatContextProvider";
import DeviceSettings from "../DeviceSettings";

function Peer({ peer, isFullScreen, onChangeFullScreen }: any) {
  const { videoRef } = useVideo({
    trackId: peer.videoTrack,
  });

  const hmsActions = useHMSActions();

  const [openDeviceSettings, setOpenDeviceSetting] = useState(false);
  const isPeerAudioEnabled = useHMSStore(selectIsPeerAudioEnabled(peer.id));
  const isPeerVideoEnabled = useHMSStore(selectIsPeerVideoEnabled(peer.id));
  const isLocalScreenshared = useHMSStore(selectIsLocalScreenShared);
  const isPeerScreenshareEnabled = useHMSStore(
    selectScreenShareByPeerID(peer.id)
  );
  const { videoRef: screenShareVideoRef } = useVideo({
    trackId: isPeerScreenshareEnabled?.id,
  });

  const {
    isLocalAudioEnabled,
    isLocalVideoEnabled,
    toggleAudio,
    toggleVideo,
  }: any = useAVToggle();

  const { finishCall, currentConversation, callState } =
    useContext(ChatContext);

  const startScreenShare = async () => {
    await hmsActions.setScreenShareEnabled(!isLocalScreenshared);
  };

  return (
    <div
      className={`peer-container rounded-xl ${
        peer.isLocal
          ? "cursor-move local absolute top-0 right-0 h-32 w-52 m-8 z-10 shadow-sm"
          : "w-full h-full absolute top-0 right-0"
      }`}
    >
      {!isLocalVideoEnabled && peer.isLocal && (
        <div
          className={`absolute top-0 left-0 h-full w-full backdrop-blur-xl rounded-lg flex justify-center items-center ${
            !isPeerVideoEnabled && peer.isLocal ? "border" : ""
          }`}
        >
          <CustomImage src={""} alt={"You"} size={60} />
        </div>
      )}

      {!isPeerVideoEnabled && !peer.isLocal && (
        <div className="absolute top-0 left-0 h-full w-full bg-gray-800 rounded-lg flex justify-center items-center">
          <CustomImage
            src={callState?.metadata?.avatar_url}
            alt={callState?.metadata?.first_name}
            size={80}
          />
        </div>
      )}

      <video
        ref={videoRef}
        className={`object-cover ${
          peer.isLocal && "h-full w-full rounded-xl overflow-hidden"
        } ${
          isPeerScreenshareEnabled && !peer.isLocal
            ? "rounded-xl overflow-hidden absolute top-0 right-0 h-32 w-52 mt-44 mx-8 z-10 shadow-sm"
            : "h-full w-full"
        }`}
        autoPlay
        muted
        playsInline
      />

      {isPeerScreenshareEnabled && !peer.isLocal && (
        <video
          ref={screenShareVideoRef}
          className={`h-full w-full object-cover`}
          autoPlay
          muted
          playsInline
        />
      )}

      <div
        className={`absolute backdrop-blur-xl rounded-lg m-2 ${
          peer.isLocal
            ? "left-0 bottom-0 px-4 py-1 text-xs"
            : "text-sm left-0 top-0 px-4 py-2"
        }`}
      >
        <div
          className={`flex items-center gap-2 ${
            !isLocalVideoEnabled && peer.isLocal ? "text-white" : ""
          } ${!isPeerVideoEnabled && !peer.isLocal ? "text-white" : ""}`}
        >
          {peer.isLocal ? peer?.name : callState?.metadata?.first_name}{" "}
          {peer.isLocal ? "(You)" : ""}
          {!peer.isLocal && (
            <div>
              {isPeerAudioEnabled ? (
                <Mic className="size-4" />
              ) : (
                <MicOff className="size-4" />
              )}
            </div>
          )}
        </div>
      </div>
      {!peer.isLocal && (
        <div className="absolute bottom-0 left-0 w-full px-0 lg:px-12 p-8 flex flex-col justify-center items-center">
          {openDeviceSettings && (
            <div className="mb-4 w-4/5 lg:w-auto">
              <DeviceSettings />
            </div>
          )}
          <div className={`flex gap-2 max-w-xl ${peer.isLocal ? "" : ""}`}>
            <div>
              <div
                onClick={toggleAudio}
                className={`cursor-pointer p-4 rounded-xl shadow-md bg-gray-800 hover:bg-gray-700 text-white`}
              >
                <SquareArrowOutDownRightIcon />
              </div>
            </div>
            <div>
              <div
                onClick={() => onChangeFullScreen()}
                className={`cursor-pointer p-4 rounded-xl shadow-md bg-gray-800 hover:bg-gray-700 text-white`}
              >
                {isFullScreen ? <ShrinkIcon /> : <ExpandIcon />}
              </div>
            </div>
            <div>
              <div
                onClick={() => toggleAudio()}
                className={`cursor-pointer p-4 rounded-xl shadow-md ${
                  isLocalAudioEnabled
                    ? "bg-gray-800 hover:bg-gray-700 text-white"
                    : "bg-white text-gray-800 hover:bg-gray-50"
                }`}
              >
                {isLocalAudioEnabled && <Mic />}
                {!isLocalAudioEnabled && <MicOff />}
              </div>
            </div>
            <div>
              <div
                onClick={() => toggleVideo()}
                className={`cursor-pointer p-4 rounded-xl shadow-md ${
                  isLocalVideoEnabled
                    ? "bg-gray-800 hover:bg-gray-700 text-white"
                    : "bg-white text-gray-800 hover:bg-gray-50"
                }`}
              >
                {isLocalVideoEnabled && <Video />}
                {!isLocalVideoEnabled && <VideoOff />}
              </div>
            </div>
            <div>
              <div
                onClick={() => startScreenShare()}
                className={`cursor-pointer  p-4 rounded-xl shadow-md ${
                  isLocalScreenshared
                    ? "bg-white text-gray-800 hover:bg-gray-50"
                    : "bg-gray-800 hover:bg-gray-700 text-white"
                }`}
              >
                {isLocalScreenshared ? <ScreenShareOff /> : <ScreenShare />}
              </div>
            </div>
            <div>
              <div
                onClick={() => setOpenDeviceSetting(!openDeviceSettings)}
                className={`cursor-pointer p-4 rounded-xl shadow-md ${
                  openDeviceSettings
                    ? "bg-white text-gray-800 hover:bg-gray-50"
                    : "bg-gray-800 hover:bg-gray-700 text-white"
                }`}
              >
                <Settings />
              </div>
            </div>
            <div>
              <div
                onClick={() => finishCall()}
                className="bg-red-500 hover:bg-red-600 p-4 text-white rounded-xl shadow-md"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.4288 14.8186L2.88441 13.3267C2.74048 13.1888 2.62693 13.0223 2.55105 12.838C2.47517 12.6536 2.43863 12.4555 2.44376 12.2562C2.44889 12.0569 2.49559 11.8609 2.58086 11.6807C2.66613 11.5005 2.78809 11.3402 2.93893 11.2098C4.64133 9.79199 6.65258 8.79324 8.81106 8.29384C10.803 7.81352 12.8763 7.77768 14.8836 8.18885C17.0671 8.61619 19.1192 9.55184 20.8737 10.92C21.0284 11.0447 21.1556 11.2002 21.247 11.3767C21.3384 11.5532 21.392 11.7467 21.4045 11.9451C21.4169 12.1434 21.388 12.3422 21.3194 12.5287C21.2509 12.7153 21.1442 12.8855 21.0063 13.0286L19.5144 14.573C19.2755 14.8252 18.9531 14.9819 18.6072 15.014C18.2613 15.046 17.9155 14.9512 17.6343 14.7472C17.0771 14.335 16.4751 13.9871 15.8396 13.7102C15.5885 13.6014 15.3737 13.423 15.2209 13.1959C15.068 12.9689 14.9835 12.7028 14.9772 12.4292L14.955 11.1438C12.9582 10.6317 10.8603 10.6679 8.88239 11.2488L8.90462 12.5342C8.90782 12.8078 8.8325 13.0767 8.68758 13.3089C8.54267 13.5411 8.33423 13.7268 8.08697 13.8442C7.4615 14.1429 6.87183 14.5114 6.3292 14.9427C6.05235 15.1586 5.70577 15.2654 5.35538 15.2425C5.00499 15.2196 4.67522 15.0687 4.4288 14.8186Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {peer.isLocal && (
        <div className="absolute bottom-0 right-0 m-2">
          <div>
            <div className="bg-gray-800 p-2 text-white rounded-xl shadow-md">
              {isPeerAudioEnabled && <Mic className="size-4" />}
              {!isPeerAudioEnabled && <MicOff className="size-4" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const VideoCallWrapper = () => {
  const { currentConversation, callState, acceptCall, rejectCall } =
    useContext(ChatContext);
  const { allDevices, selectedDeviceIDs, updateDevice } = useDevices();
  const { videoInput, audioInput, audioOutput } = allDevices;
  const [remoteUserId, setRemoteUserId] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [showDeclineMessage, setShowDeclineMessage] = useState<boolean>(false);

  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const peers = useHMSStore(selectPeers);
  const presenters = useHMSStore(selectPeersScreenSharing);

  const { isMobile } = useMobile();
  const divRef = useRef<any>(null);

  const handleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (divRef.current) {
        divRef.current?.requestFullscreen().catch((err: any) => {
          console.error("Failed to enable full-screen mode:", err);
        });
      }
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <Fragment>
      <div
        className={`h-full w-full max-h-lvh z-10 ${
          callState?.state === "incoming"
            ? "col-span-3"
            : callState?.state === "accepted"
            ? "col-span-7"
            : ""
        } ${isMobile ? "absolute" : "relative"}`}
      >
        {callState?.state === "incoming" && (
          <div className="flex h-full w-full items-end justify-end">
            <div className="bg-background rounded-xl text-sm shadow-sm w-full cursor-pointer">
              <div className="h-full w-full flex flex-col rounded-xl p-4">
                <div className="flex-1 flex flex-col justify-center items-center space-y-6 mb-8">
                  <div className="relative h-16 w-16">
                    <div className="absolute h-16 w-16 rounded-full inset-0 text-secondary-500 call-animation"></div>
                    <div className="relative h-full w-full flex items-center justify-center">
                      <CustomImage
                        src={callState?.metadata?.avatar_url}
                        alt={callState?.metadata?.first_name}
                        size={60}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold text-sm">
                      {callState?.metadata?.first_name} is calling you
                    </div>
                    <div className="font-mediumopacity-75 capitalize">
                      {callState?.metadata?.workspace_name} support
                    </div>
                  </div>
                </div>
                <div>
                  {!showDeclineMessage && (
                    <div className="flex justify-around w-full p-4">
                      <div
                        className="space-y-3"
                        onClick={() => setShowDeclineMessage(true)}
                      >
                        <div className="h-12 w-12 bg-red-400 text-white rounded-xl flex justify-center items-center">
                          <X className="size-5" />
                        </div>
                        <div className="font-medium text-sm">Decline</div>
                      </div>
                      <div className="space-y-3" onClick={() => acceptCall()}>
                        <div className="h-12 w-12 bg-green-400 text-white rounded-xl flex justify-center items-center">
                          <PhoneCall className="size-5" />
                        </div>
                        <div className="font-medium text-sm">Accept</div>
                      </div>
                    </div>
                  )}

                  {showDeclineMessage && (
                    <div className="space-y-4 p-4">
                      <div className="font-mediumopacity-75 text-lg font-medium">
                        Are you sure you want to decline this call?
                      </div>
                      <div className="flex flex-col gap-2">
                        <div>
                          <div
                            onClick={() => acceptCall()}
                            className="font-semibold text-xs p-4 rounded-lg border hover:bg-secondary-500 hover:text-white text-center"
                          >
                            No, Accept
                          </div>
                        </div>
                        <div>
                          <div
                            onClick={() => rejectCall()}
                            className="font-semibold text-xs p-4 rounded-lg border hover:bg-secondary-500 hover:text-white text-center"
                          >
                            Yes, Decline
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="relative w-full h-full" ref={divRef}>
          {peers.map((peer: any, idx: number) => (
            <Peer
              peer={peer}
              key={idx}
              isFullScreen={document.fullscreenElement}
              onChangeFullScreen={() => handleFullScreen()}
            />
          ))}
        </div>
      </div>
    </Fragment>
  );
};

const useMobile = () => {
  const [isMobile, setMobile] = useState(false);

  useEffect(() => {
    const applyResponsiveClasses = () => {
      if (window.parent) {
        const parentWidth = window.parent.innerWidth;

        if (parentWidth < 768) {
          setMobile(true);
        } else {
          setMobile(false);
        }
      }
    };

    applyResponsiveClasses();
    window.parent.addEventListener("resize", applyResponsiveClasses);

    return () => {
      window.parent.removeEventListener("resize", applyResponsiveClasses);
    };
  }, []);

  return { isMobile };
};

export default VideoCallWrapper;
